import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { saveAs } from "file-saver";

// Export helpers for the home report modals.
//   PDF  — snapshot the whole report body (KPI tiles + charts) to an image and
//          lay it into an A4 document, paginating when it's taller than a page.
//          Same html-to-image + jsPDF approach already used by the Visit Data
//          Summary export.
//   Word — build a Word-compatible HTML document (.doc) with a real KPI table
//          (editable text) plus each chart captured as an embedded PNG. No extra
//          dependency needed — Word opens application/msword HTML natively.

const PNG_OPTS = { cacheBust: true, backgroundColor: "#ffffff", pixelRatio: 2 };

const safeFileName = (title) =>
  (title || "report").replace(/[^\w-]+/g, "_").replace(/_+/g, "_");

// Wait two animation frames so the browser has painted the (animation-disabled)
// charts before we snapshot them.
const nextPaint = () =>
  new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );

export async function exportReportToPdf(node, { title, meta }) {
  if (!node) return;
  await nextPaint();

  const dataUrl = await toPng(node, PNG_OPTS);
  const pdf = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 12;

  // Title + metadata header.
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.setTextColor(106, 17, 60);
  pdf.text(title || "Report", margin, margin + 4);
  if (meta) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 120);
    pdf.text(meta, margin, margin + 10);
  }

  const topOffset = margin + 16;
  const imgW = pageW - margin * 2;
  const imgH = (node.offsetHeight / node.offsetWidth) * imgW;

  let position = topOffset;
  let heightLeft = imgH;

  pdf.addImage(dataUrl, "PNG", margin, position, imgW, imgH);
  heightLeft -= pageH - position - margin;

  // Continue onto further pages, shifting the same image up each time.
  while (heightLeft > 0) {
    pdf.addPage();
    position = margin - (imgH - heightLeft);
    pdf.addImage(dataUrl, "PNG", margin, position, imgW, imgH);
    heightLeft -= pageH - margin * 2;
  }

  pdf.save(`${safeFileName(title)}.pdf`);
}

export async function exportReportToWord(node, { title, meta, kpis = [] }) {
  if (!node) return;
  await nextPaint();

  // Capture each chart card (title + chart together) as its own image.
  const cards = Array.from(node.querySelectorAll(".reportChartCard"));
  const images = [];
  for (const card of cards) {
    // eslint-disable-next-line no-await-in-loop
    const url = await toPng(card, PNG_OPTS);
    images.push(url);
  }

  const kpiRows = kpis
    .map(
      (k) =>
        `<tr><td style="padding:6px 12px;border:1px solid #e6dbe2;">${
          k.label
        }</td><td style="padding:6px 12px;border:1px solid #e6dbe2;font-weight:bold;color:${
          k.color || "#6A113C"
        };">${k.value}${k.suffix || ""}</td></tr>`
    )
    .join("");

  const imgHtml = images
    .map(
      (u) =>
        `<div style="margin:14px 0;"><img src="${u}" style="width:100%;max-width:640px;"/></div>`
    )
    .join("");

  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>${title || "Report"}</title></head>
<body style="font-family:Arial, sans-serif;color:#242424;">
  <h1 style="color:#6A113C;margin:0 0 4px;">${title || "Report"}</h1>
  <p style="color:#707073;margin:0 0 18px;font-size:13px;">${meta || ""}</p>
  ${
    kpiRows
      ? `<h2 style="color:#6A113C;font-size:16px;">Key figures</h2>
  <table style="border-collapse:collapse;font-size:13px;margin-bottom:8px;">${kpiRows}</table>`
      : ""
  }
  ${imgHtml ? `<h2 style="color:#6A113C;font-size:16px;">Charts</h2>${imgHtml}` : ""}
</body></html>`;

  const blob = new Blob(["﻿", html], { type: "application/msword" });
  saveAs(blob, `${safeFileName(title)}.doc`);
}
